import React from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';

import { ContentSet, LabelBeta } from 'components/Shared/Scaffolding';
import StylePreview from 'components/StyleSettings/StylePreview';
import LocationPreview from 'components/LocationSettings/LocationPreview';
import LocationPanel from 'components/LocationSettings/LocationPanel';
import MarkerPreviewContainer from 'containers/MarkerPreviewContainer';
import MarkerPanel from 'components/MarkerSettings/MarkerPanel';
import InfoWindowPanel from 'components/InfoWindowSettings/InfoWindowPanel';
import AdvancedPreview from 'components/AdvancedSettings/AdvancedPreview';
import AdvancedPanel from 'components/AdvancedSettings/AdvancedPanel';
import StylePanel from 'components/StyleSettings/StylePanel';
import SavePanel from 'components/Save/SavePanel';
import ExportPanel from 'components/Export/ExportPanel';
import HelpPanel from 'components/Help/HelpPanel';
import Panel, { PanelTitle, TRANSITION_TYPE } from 'components/Shared/Panel';
import { PANELS, changePanel, closePanel, getActivePanel } from 'modules/navigation/activePanel';
import { clearPreviewLocation } from 'modules/navigation/preview';
import { BREAKPOINTS, SCAFFOLDING, TRANSITION } from 'theme';

const SideMenuWrapper = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${SCAFFOLDING.COLUMN_WIDTH(5)};
    min-height: 100%;
    z-index: 2;
    overflow: hidden;
    background-color: ${SCAFFOLDING.BG};
    box-shadow: ${SCAFFOLDING.BOX_SHADOW.BASE};
    transition: left ${TRANSITION.LENGTH_LONG} ${TRANSITION.BEZIER};

    @media (min-width: ${`${BREAKPOINTS.lg}px`}) {
        width: ${SCAFFOLDING.COLUMN_WIDTH(4)};
    }

    @media (max-width: ${`${BREAKPOINTS.md - 1}px`}) {
        display: none;
    }

    ${ifProp(
        'fullscreen',
        css`
            left: -${SCAFFOLDING.COLUMN_WIDTH(4)} !important;
        `
    )}
`;

const StyledPanelTitle = styled(PanelTitle)`
    display: inline-block;
    margin-right: 8px;
    vertical-align: middle;
`;

const NumberedContentSet = styled(ContentSet)`
    counter-reset: preview-counter;
`;

const NumberedSettingsGroup = styled.div`
    position: relative;

    .label-counter {
        &:before {
            content: counter(preview-counter);
            counter-increment: preview-counter;
            flex: 0 0 auto;
            align-self: center;
            display: inline-block;
            width: ${SCAFFOLDING.GUTTER_FACTOR(0.7)};
            height: ${SCAFFOLDING.GUTTER_FACTOR(0.7)};
            margin-right: ${SCAFFOLDING.GUTTER_FACTOR(0.25)};
            background-color: #aaa;
            border-radius: 50%;
            color: #fff;
            font-size: 1.1em;
            line-height: ${SCAFFOLDING.GUTTER_FACTOR(0.7)};
            text-align: center;
        }
    }

    &:not(:last-child) {
        .label-counter:after {
            content: '';
            display: block;
            position: absolute;
            top: ${SCAFFOLDING.GUTTER_FACTOR(0.7)};
            left: ${SCAFFOLDING.GUTTER_FACTOR(0.35)};
            width: 2px;
            height: calc(100% + ${SCAFFOLDING.GUTTER_FACTOR(0.8)});
            margin-left: -1px;
            background-color: #ddd;
        }
    }

    .content-counter {
        padding-left: ${SCAFFOLDING.GUTTER_FACTOR(2)};
    }
`;

const NumberedStylePreview = NumberedSettingsGroup.withComponent(StylePreview);
const NumberedLocationPreview = NumberedSettingsGroup.withComponent(LocationPreview);
const NumberedMarkerPreview = NumberedSettingsGroup.withComponent(MarkerPreviewContainer);
const NumberedAdvancedPreview = NumberedSettingsGroup.withComponent(AdvancedPreview);

const SideMenu = ({ activePanel, onPanelClose, onPanelOpen, fullscreen }) => {
    return (
        <SideMenuWrapper fullscreen={fullscreen}>
            <Panel main open={activePanel === PANELS.MAIN} onClose={onPanelClose} transition={TRANSITION_TYPE.FADE}>
                <NumberedContentSet spacing={SCAFFOLDING.GUTTER} padding={SCAFFOLDING.GUTTER}>
                    <header>
                        <StyledPanelTitle>Build a Map</StyledPanelTitle>
                        <LabelBeta>Beta</LabelBeta>
                    </header>
                    <p>
                        Create a map using the sections below. Once you are finished, you can copy the code and embed
                        the map on your site.
                    </p>
                    <NumberedStylePreview
                        onCardClick={() => onPanelOpen(PANELS.STYLE)}
                        labelClassName="label-counter"
                        contentClassName="content-counter"
                    />
                    <NumberedLocationPreview
                        onCardClick={() => onPanelOpen(PANELS.LOCATION)}
                        labelClassName="label-counter"
                        contentClassName="content-counter"
                    />
                    <NumberedMarkerPreview labelClassName="label-counter" contentClassName="content-counter" />
                    <NumberedAdvancedPreview
                        onCardClick={() => onPanelOpen(PANELS.ADVANCED_SETTINGS)}
                        labelClassName="label-counter"
                        contentClassName="content-counter"
                    />
                </NumberedContentSet>
            </Panel>
            <StylePanel open={activePanel === PANELS.STYLE} onClose={onPanelClose} />
            <LocationPanel open={activePanel === PANELS.LOCATION} onClose={onPanelClose} />
            <MarkerPanel open={activePanel === PANELS.MARKER} onClose={onPanelClose} />
            <InfoWindowPanel open={activePanel === PANELS.INFO_WINDOW} onClose={onPanelClose} />
            <AdvancedPanel open={activePanel === PANELS.ADVANCED_SETTINGS} onClose={onPanelClose} />
            <SavePanel open={activePanel === PANELS.SAVE} onClose={onPanelClose} />
            <ExportPanel open={activePanel === PANELS.EXPORT} onClose={onPanelClose} />
            <HelpPanel open={activePanel === PANELS.HELP} onClose={onPanelClose} />
        </SideMenuWrapper>
    );
};

const mapStateToProps = (state) => ({
    activePanel: getActivePanel(state),
    fullscreen: state.navigation.fullscreen,
});

const mapDispatchToProps = (dispatch) => ({
    onPanelOpen: (panel) => {
        if (panel === PANELS.LOCATION) {
            dispatch(clearPreviewLocation());
        }
        dispatch(changePanel(panel));
    },
    onPanelClose: () => dispatch(closePanel()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
